<template>
  <div class="gtc-modal">
    <a-modal
      :open="open"
      v-bind="{ ...normalizedAttrs, wrapClassName: `gtc-modal-wrap ${attrs.wrapClassName}` }"
      @cancel="handleCancel"
      @ok="handleOk"
    >
      <template
        v-for="slotName in Object.keys(slots)"
        v-slot:[slotName]="slotProps"
        :key="slotName"
      >
        <slot :name="slotName" v-bind="slotProps ?? {}"></slot>
      </template>
      <template #footer v-if="!slots.footer">
        <div class="flex flex-col gap-6">
          <div class="flex items-center justify-end">
            <GtcButton
              v-if="props.showCancelButton"
              mode="light"
              type="primary"
              size="xl"
              @click="handleCancel"
              >{{ cancelText }}</GtcButton
            >
            <GtcButton
              type="primary"
              size="xl"
              :disabled="disabled || mergedLoading"
              :loading="mergedLoading"
              @click="handleOk"
              >{{ okText }}</GtcButton
            >
          </div>
        </div>
      </template>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { computed, useAttrs, useSlots } from 'vue'
import { GtcButton } from '@/components'
import { useI18n } from 'vue-i18n'

const slots = useSlots()
const attrs = useAttrs()
const emit = defineEmits(['cancel', 'ok'])

const normalizedAttrs = computed(() => {
  const a = { ...attrs }
  if (slots.title) delete (a as any).title
  // 将 confirmLoading 透传给 a-modal，便于使用其默认 footer 时也能工作
  if ((a as any).confirmLoading === undefined) {
    ;(a as any).confirmLoading = mergedLoading.value
  }
  return a
})

// 直接使用 defineModel，不需要额外的 ref 和 watch
const props = withDefaults(
  defineProps<{
    open: boolean
    disabled?: boolean
    loading?: boolean
    confirmLoading?: boolean
    okText?: string
    cancelText?: string
    showCancelButton?: boolean
  }>(),
  {
    open: false,
    disabled: false,
    loading: false,
    confirmLoading: false,
    showCancelButton: true,
  },
)

const { t } = useI18n()

const okText = computed(() => {
  return props.okText ? props.okText : t('components.gtcModal.okText')
})

const cancelText = computed(() => {
  return props.cancelText ? props.cancelText : t('components.gtcModal.cancelText')
})

// 合并 loading：同时支持自定义 footer 的 loading 和 antd 的 confirmLoading 命名
const mergedLoading = computed(() => props.loading || props.confirmLoading)

const handleCancel = () => {
  emit('cancel')
}

const handleOk = () => {
  emit('ok')
}
</script>

<style lang="less">
.gtc-modal-wrap {
  .ant-modal-header {
    background: none;
    margin-bottom: 0;
    .ant-modal-title {
      font-size: 20px;
      color: var(--ink-950);
    }
  }

  .ant-modal-content {
    border-radius: 16px;
    background: var(--indicator-model, #101b36);
  }

  .ant-modal-close {
    top: 24px;
    right: 24px;
  }

  .ant-modal-footer {
    margin-top: 0;
    // .ant-btn-default {
    //   background: var(--blue-primary-bg);
    //   border: none;
    // }
    .ant-btn + .ant-btn:not(.ant-dropdown-trigger) {
      margin-inline-start: 20px;
    }
  }
}
</style>
